import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import LocalStorageService from '@application/common/local-storage-service';
import { APIStatus } from '@application/enums/api-status';
import { getLanguageCodeForCountry } from '@application/utilities/country-language';
import { getEventLanguagesByEventIdAsync } from '@application/features/languages/languagesThunk';
import { getVDKEventByIdForRegistrationAsync, getVDKEventUserCountryAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { resetUserCountry } from '@application/features/vdkEvent/VDKEventSlice';
import { getFaqsByEventIdForRegistrationAsync } from '@application/features/faqCategory/faqCategoryThunk';
const syncI18nLanguage = (code, i18n) => {
    if (!code)
        return;
    const normalized = String(code).toLowerCase().split('-')[0];
    const current = (i18n.language || '').toLowerCase();
    if (!(current === normalized || current.startsWith(`${normalized}-`))) {
        i18n.changeLanguage(normalized);
    }
};
export const useEventRegistrationLanguage = ({ embeddedPreview = false, i18n }) => {
    const { e } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlLanguageId = searchParams.get('language');
    const requestedLanguageKeyRef = useRef(undefined);
    const dispatch = useAppDispatch();
    const { eventLanguages } = useAppSelector((state) => state.languages);
    const { VDKEventForRegistration, userCountryCode, userCountryStatus } = useAppSelector((state) => state.VDKEvent);
    const [selectedLanguageId, setSelectedLanguageId] = useState(undefined);
    const [registrationDataLanguageId, setRegistrationDataLanguageId] = useState(undefined);
    useEffect(() => {
        if (e && (!eventLanguages || eventLanguages.length === 0)) {
            dispatch(getEventLanguagesByEventIdAsync(e));
        }
    }, [dispatch, e, eventLanguages]);
    useEffect(() => {
        dispatch(resetUserCountry());
    }, [dispatch, e]);
    const needsIpLookup = useMemo(() => {
        if (embeddedPreview || !e)
            return false;
        if (!Array.isArray(eventLanguages) || eventLanguages.length === 0)
            return false;
        if (eventLanguages.length === 1)
            return false;
        if (urlLanguageId && eventLanguages.some((language) => language.id === urlLanguageId)) {
            return false;
        }
        // The stored language only skips the IP lookup when the user picked it
        // manually; an auto-detected value must not freeze the language, so a VPN
        // or location change is reflected on the next visit.
        if (LocalStorageService.getEventLanguageManualOverride(e)) {
            const storedLanguageId = LocalStorageService.getEventLanguage(e);
            if (storedLanguageId && eventLanguages.some((language) => language.id === storedLanguageId)) {
                return false;
            }
        }
        return true;
    }, [embeddedPreview, e, eventLanguages, urlLanguageId]);
    useEffect(() => {
        if (!needsIpLookup || userCountryStatus !== APIStatus.IDLE)
            return;
        dispatch(getVDKEventUserCountryAsync());
    }, [needsIpLookup, userCountryStatus, dispatch]);
    const ipLookupComplete = userCountryStatus === APIStatus.SUCCESS || userCountryStatus === APIStatus.FAILED;
    const resolveLanguageId = useCallback(() => {
        if (!Array.isArray(eventLanguages) || eventLanguages.length === 0) {
            return undefined;
        }
        if (urlLanguageId && eventLanguages.some((language) => language.id === urlLanguageId)) {
            return urlLanguageId;
        }
        if (e && LocalStorageService.getEventLanguageManualOverride(e)) {
            const storedLanguageId = LocalStorageService.getEventLanguage(e);
            if (storedLanguageId && eventLanguages.some((language) => language.id === storedLanguageId)) {
                return storedLanguageId;
            }
        }
        if (typeof userCountryCode === 'string' && userCountryCode) {
            const targetCode = getLanguageCodeForCountry(userCountryCode);
            const ipMatch = eventLanguages.find((language) => {
                const code = (language.code || '').toLowerCase();
                return code === targetCode || code.startsWith(`${targetCode}-`);
            });
            if (ipMatch)
                return ipMatch.id;
        }
        const primary = eventLanguages.find((language) => language.isPrimary === true);
        return primary?.id || eventLanguages[0]?.id;
    }, [eventLanguages, urlLanguageId, e, userCountryCode]);
    useEffect(() => {
        if (selectedLanguageId)
            return;
        if (needsIpLookup && !ipLookupComplete)
            return;
        const resolvedLanguageId = resolveLanguageId();
        if (!resolvedLanguageId || !e)
            return;
        setSelectedLanguageId(resolvedLanguageId);
        LocalStorageService.setEventLanguage(e, resolvedLanguageId);
        const lang = eventLanguages?.find((language) => language.id === resolvedLanguageId);
        if (!embeddedPreview) {
            syncI18nLanguage(lang?.code, i18n);
        }
    }, [
        resolveLanguageId,
        selectedLanguageId,
        eventLanguages,
        e,
        i18n,
        embeddedPreview,
        needsIpLookup,
        ipLookupComplete
    ]);
    useEffect(() => {
        if (!urlLanguageId || !Array.isArray(eventLanguages) || eventLanguages.length === 0 || !e) {
            return;
        }
        if (!eventLanguages.some((language) => language.id === urlLanguageId)) {
            return;
        }
        if (urlLanguageId === selectedLanguageId) {
            return;
        }
        setSelectedLanguageId(urlLanguageId);
        LocalStorageService.setEventLanguage(e, urlLanguageId);
        const lang = eventLanguages.find((language) => language.id === urlLanguageId);
        if (!embeddedPreview) {
            syncI18nLanguage(lang?.code, i18n);
        }
    }, [urlLanguageId, eventLanguages, selectedLanguageId, e, i18n, embeddedPreview]);
    // Single place that loads registration data + FAQs for the selected language.
    // The ref guards against re-firing while a request is in flight (the store update
    // lands before setRegistrationDataLanguageId), which previously caused duplicate calls.
    useEffect(() => {
        if (!e || !selectedLanguageId)
            return;
        const requestKey = `${e}:${selectedLanguageId}`;
        if (requestedLanguageKeyRef.current === requestKey)
            return;
        if (VDKEventForRegistration && registrationDataLanguageId === selectedLanguageId) {
            return;
        }
        requestedLanguageKeyRef.current = requestKey;
        const fetchRegistrationData = async () => {
            try {
                const request = {
                    id: e,
                    languageId: selectedLanguageId
                };
                await dispatch(getVDKEventByIdForRegistrationAsync(request)).unwrap();
                if (requestedLanguageKeyRef.current !== requestKey)
                    return;
                setRegistrationDataLanguageId(selectedLanguageId);
                await dispatch(getFaqsByEventIdForRegistrationAsync({
                    eventId: e,
                    languageId: selectedLanguageId
                }));
            }
            catch {
                if (requestedLanguageKeyRef.current === requestKey) {
                    requestedLanguageKeyRef.current = undefined;
                }
            }
        };
        void fetchRegistrationData();
    }, [
        dispatch,
        e,
        selectedLanguageId,
        VDKEventForRegistration,
        registrationDataLanguageId
    ]);
    const handleLanguageChange = useCallback((languageId) => {
        // Fetching happens in the effect above once selectedLanguageId changes
        setSelectedLanguageId(languageId);
        if (e) {
            LocalStorageService.setEventLanguage(e, languageId);
            LocalStorageService.setEventLanguageManualOverride(e);
        }
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('language', languageId);
        setSearchParams(nextSearchParams, { replace: true });
        const lang = (eventLanguages || []).find((language) => language.id === languageId);
        if (!embeddedPreview) {
            syncI18nLanguage(lang?.code, i18n);
        }
    }, [e, embeddedPreview, eventLanguages, i18n, searchParams, setSearchParams]);
    const selectedLanguageCode = (eventLanguages ?? []).find((language) => language.id === selectedLanguageId)?.code;
    return {
        embeddedPreview,
        eventLanguages: eventLanguages ?? [],
        selectedLanguageId,
        selectedLanguageCode,
        registrationDataLanguageId,
        setSelectedLanguageId,
        handleLanguageChange
    };
};
